import { useContext, useRef, useState } from 'react';
import ProTable, { ActionType, ProColumns } from '@ant-design/pro-table';
import { Button, Switch } from 'antd';
import { ContextApp } from '../../contexts/ContextApp';
import { notificationContext } from '../../contexts/NotificationContext';
import GraphqlService from '../../services/graphql/GraphqlService';
import { Query } from '../../services/graphql/query';
import { Mutation } from '../../services/graphql/mutation';
import { CustomMessage } from '../../hooks';
import { ITenantSetting } from '../../interfaces/TenantSetting';
import './TenantSetting.less';

interface IVisibilityChange {
  id: number;
  is_private: boolean;
}

export const ManageTenantSettings = () => {
  const { t } = useContext(ContextApp);
  const { openNotification } = useContext(notificationContext);
  const { customRequest } = GraphqlService();
  const { getErrorMessage } = CustomMessage();

  const actionRef = useRef<ActionType>();
  const [pendingChanges, setPendingChanges] = useState<
    Map<number, IVisibilityChange>
  >(new Map());
  const [dataSource, setDataSource] = useState<ITenantSetting[]>([]);

  const request = async () => {
    try {
      const data: ITenantSetting[] = await customRequest({
        query: Query.tenantSettings,
        variables: {},
      });
      data.sort((a, b) => a.id - b.id);
      setDataSource(data);
      setPendingChanges(new Map());
      return {
        current: 1,
        data,
        pageSize: '1',
        success: true,
        total: data.length,
      };
    } catch (error) {
      return {
        current: 1,
        data: [],
        pageSize: '1',
        success: true,
        total: 0,
      };
    }
  };

  const handleToggle = (record: ITenantSetting, checked: boolean) => {
    const newChanges = new Map(pendingChanges);
    newChanges.set(record.id, { id: record.id, is_private: checked });
    setPendingChanges(newChanges);
    setDataSource((prev) =>
      prev.map((s) => (s.id === record.id ? { ...s, is_private: checked } : s)),
    );
  };

  const handleSave = async () => {
    if (pendingChanges.size === 0) return;
    try {
      const input = Array.from(pendingChanges.values());
      await customRequest({
        mutation: Mutation.updateTenantSettingsVisibility,
        variables: { input },
      });
      openNotification({
        msj: t('message.updateSuccess'),
        type: 'success',
      });
      setPendingChanges(new Map());
      if (actionRef.current) {
        actionRef.current.reload();
      }
    } catch (error: any) {
      openNotification({
        msj: getErrorMessage(error),
        type: 'error',
      });
    }
  };

  const columns: ProColumns<ITenantSetting>[] = [
    {
      title: t('entity.name'),
      dataIndex: 'name',
      key: 'name',
      hideInSearch: true,
      render: (_, record) => <span translate="no">{record.name}</span>,
    },
    {
      title: t('entity.description'),
      dataIndex: 'description',
      key: 'description',
      hideInSearch: true,
      render: (_, record) => record.description || '',
    },
    {
      title: t('tenant_setting.visible'),
      dataIndex: 'is_private',
      key: 'is_private',
      hideInSearch: true,
      width: 120,
      align: 'center',
      render: (_, record) => {
        const currentValue =
          dataSource.find((s) => s.id === record.id)?.is_private ??
          record.is_private;
        return (
          <Switch
            checked={!currentValue}
            onChange={(checked) => handleToggle(record, !checked)}
          />
        );
      },
    },
  ];

  return (
    <>
      <div className="protable-tenant-setting--wrapper">
        <ProTable<ITenantSetting>
          className="protable-tenant-setting"
          actionRef={actionRef}
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          rowKey="id"
          search={false}
          options={{ reload: false, setting: false, density: false }}
          request={async () => request()}
        />
      </div>
      <div className="tenant-setting-save-button">
        <Button
          className={`btn-save ${pendingChanges.size === 0 ? 'disabled' : ''}`}
          disabled={pendingChanges.size === 0}
          onClick={handleSave}
        >
          {t('action.save')}
        </Button>
      </div>
    </>
  );
};
